import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Send, Film, User, Mail, FileText, Link, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  
  const formRef = useRef(null);
  const isFormInView = useInView(formRef, { once: true, margin: '-50px' });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectTitle: '',
    category: '',
    synopsis: '',
    videoLink: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        projectTitle: '',
        category: '',
        synopsis: '',
        videoLink: '',
        message: '',
      });
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const categories = [
    'Long métrage fiction',
    'Court métrage fiction',
    'Documentaire',
    'Animation',
    'Autre',
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-[#050505] relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[150px]" />
          </div>
          
          <div className="container-custom relative z-10">
            <motion.div
              ref={heroRef}
              initial={{ opacity: 0, y: 30 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <span className="overline mb-6 block">Contact</span>
              <h1 className="text-4xl md:text-6xl font-medium mb-6 leading-tight">
                Proposez votre <span className="text-gradient">projet</span>
              </h1>
              <p className="text-[#a1a1aa] text-lg md:text-xl leading-relaxed max-w-3xl">
                Vous avez un projet cinématographique porteur de sens ? 
                Présentez-nous votre œuvre et rejoignez l'aventure Second Souffle.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-16 md:py-24 bg-[#0a0a0c]" ref={formRef}>
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
              
              {/* Left - Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={isFormInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6 }}
                className="lg:col-span-4"
              >
                <div className="sticky top-32">
                  <h2 className="text-2xl font-medium text-white mb-6">
                    Ce que nous recherchons
                  </h2>
                  
                  <div className="space-y-6">
                    {[
                      { 
                        title: 'Engagement environnemental', 
                        desc: 'Films qui éveillent les consciences sur les enjeux écologiques.' 
                      },
                      { 
                        title: 'Impact social', 
                        desc: 'Récits porteurs d\'inclusion, de diversité et d\'égalité.' 
                      },
                      { 
                        title: 'Singularité artistique', 
                        desc: 'Œuvres uniques qui questionnent et inspirent.' 
                      },
                    ].map((item, index) => (
                      <motion.div
                        key={item.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={isFormInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                        className="flex gap-4"
                      >
                        <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-[#D4AF37]" />
                        <div>
                          <h3 className="text-white font-medium mb-1">{item.title}</h3>
                          <p className="text-[#71717a] text-sm">{item.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-10 p-6 rounded-2xl bg-white/5 border border-white/5">
                    <Mail size={24} className="text-[#D4AF37] mb-3" />
                    <p className="text-[#a1a1aa] text-sm">
                      Vous pouvez aussi nous contacter directement à
                    </p>
                    <a 
                      href="mailto:contact@secondsouffleprod.com" 
                      className="text-[#D4AF37] hover:text-[#F3E5AB] transition-colors font-medium"
                    >
                      contact@secondsouffleprod.com
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Right - Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={isFormInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-8"
              >
                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="glass rounded-3xl p-12 text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", delay: 0.2 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center"
                    >
                      <CheckCircle size={40} className="text-[#D4AF37]" />
                    </motion.div>
                    <h2 className="text-2xl font-medium text-white mb-4">
                      Merci pour votre soumission !
                    </h2>
                    <p className="text-[#a1a1aa] mb-8">
                      Nous avons bien reçu votre projet. Notre équipe l'examinera 
                      avec attention et reviendra vers vous prochainement.
                    </p>
                    <button
                      onClick={() => setIsSubmitted(false)}
                      className="btn-secondary"
                    >
                      Soumettre un autre projet
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="glass rounded-3xl p-8 md:p-10">
                    <h2 className="text-2xl font-medium text-white mb-8">
                      Formulaire de soumission
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {/* Nom */}
                      <div>
                        <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                          <User size={14} className="inline mr-2" />
                          Votre nom *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                          placeholder="Prénom Nom"
                          data-testid="input-name"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                          <Mail size={14} className="inline mr-2" />
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                          placeholder="votre@email.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      {/* Titre du projet */}
                      <div>
                        <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                          <Film size={14} className="inline mr-2" />
                          Titre du projet *
                        </label>
                        <input
                          type="text"
                          name="projectTitle"
                          value={formData.projectTitle}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                          placeholder="Titre de votre film"
                          data-testid="input-project-title"
                        />
                      </div>

                      {/* Catégorie */}
                      <div>
                        <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                          <FileText size={14} className="inline mr-2" />
                          Catégorie *
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#D4AF37] transition-colors appearance-none cursor-pointer"
                          data-testid="select-category"
                        >
                          <option value="" className="bg-[#121215]">Sélectionnez...</option>
                          {categories.map(cat => (
                            <option key={cat} value={cat} className="bg-[#121215]">{cat}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Lien vidéo */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                        <Link size={14} className="inline mr-2" />
                        Lien vers le projet (Vimeo, YouTube, etc.)
                      </label>
                      <input
                        type="url"
                        name="videoLink"
                        value={formData.videoLink}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                        placeholder="https://..."
                        data-testid="input-video-link"
                      />
                    </div>

                    {/* Synopsis */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                        Synopsis / Pitch *
                      </label>
                      <textarea
                        name="synopsis"
                        value={formData.synopsis}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                        placeholder="Présentez brièvement votre projet..."
                        data-testid="textarea-synopsis"
                      />
                    </div>

                    {/* Message */}
                    <div className="mb-8">
                      <label className="block text-sm font-medium text-[#a1a1aa] mb-2">
                        Message complémentaire
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                        placeholder="Informations supplémentaires..."
                        data-testid="textarea-message"
                      />
                    </div>

                    {error && (
                      <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 size={20} className="animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Envoyer ma soumission
                        </>
                      )}
                    </button>

                    <p className="text-[#71717a] text-xs text-center mt-4">
                      En soumettant ce formulaire, vous acceptez que vos données soient traitées 
                      par Second Souffle Production dans le cadre de l'étude de votre projet.
                    </p>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
